import React, { useState, useEffect } from 'react';
import { X, Search, Image as ImageIcon, Link as LinkIcon, Tag, Check, Plus, Film } from 'lucide-react';
import api from '../services/cloudflare-api';

/**
 * MediaPicker - Select media from D1 library OR add new URL
 * Used by EditableMedia component for selecting images/videos
 */
export default function MediaPicker({ isOpen, onClose, onSelect, currentUrl, mediaType = 'all' }) {
  const [mediaItems, setMediaItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedUrl, setSelectedUrl] = useState(currentUrl || '');
  const [showAddUrl, setShowAddUrl] = useState(false);

  // Add URL form state
  const [urlInput, setUrlInput] = useState('');
  const [urlName, setUrlName] = useState('');
  const [urlCategory, setUrlCategory] = useState('general');
  const [urlAltText, setUrlAltText] = useState('');
  const [adding, setAdding] = useState(false);

  const categories = [
    { value: 'all', label: 'All Media' },
    { value: 'general', label: 'General' },
    { value: 'hero', label: 'Hero Images' },
    { value: 'service', label: 'Service Pages' },
    { value: 'comparison', label: 'Before/After' },
    { value: 'testimonial', label: 'Testimonials' },
    { value: 'team', label: 'Team Members' },
    { value: 'gallery', label: 'Gallery' }
  ];

  useEffect(() => {
    if (isOpen) {
      loadMediaItems();
      setSelectedUrl(currentUrl || '');
    }
  }, [isOpen, currentUrl, selectedCategory, searchQuery]);

  const loadMediaItems = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedCategory !== 'all') {
        params.category = selectedCategory;
      }
      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }

      const items = await api.getMediaItems(params);
      setMediaItems(Array.isArray(items) ? items : []);
    } catch (error) {
      console.error('Error loading media items:', error);
      setMediaItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUrl = async () => {
    if (!urlInput.trim() || !urlName.trim()) {
      alert('Please enter both URL and name');
      return;
    }

    setAdding(true);
    try {
      const newItem = await api.createMediaItem({
        name: urlName.trim(),
        url: urlInput.trim(),
        category: urlCategory,
        alt_text: urlAltText.trim() || null,
        size: 0,
        is_external: true
      });

      console.log('✅ Created media item:', newItem);

      // Select the newly added item
      setSelectedUrl(newItem.url);

      // Reset form
      setUrlInput('');
      setUrlName('');
      setUrlCategory('general');
      setUrlAltText('');
      setShowAddUrl(false);

      // Reload media items
      await loadMediaItems();
      alert('Media URL added to library!');
    } catch (error) {
      console.error('Error adding URL:', error);
      alert(`Failed to add URL: ${error.message}`);
    } finally {
      setAdding(false);
    }
  };

  const handleQuickSelect = (url) => {
    onSelect(url);
    onClose();
  };

  const handleConfirmSelection = () => {
    if (selectedUrl) {
      onSelect(selectedUrl);
      onClose();
    } else {
      alert('Please select a media item or add a new URL');
    }
  };

  const isVideo = (url) => {
    return url && (url.includes('.mp4') || url.includes('.webm') || url.includes('.mov') || url.includes('video'));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 99999 }}>
      <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Select Media</h2>
            <p className="text-sm text-gray-600 mt-1">Choose from library or add new URL</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Category Filter */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2 overflow-x-auto">
            {categories.map(cat => (
              <button
                key={cat.value}
                onClick={() => setSelectedCategory(cat.value)}
                className={`px-4 py-2 rounded-lg whitespace-nowrap transition-colors text-sm ${
                  selectedCategory === cat.value
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* Search and Actions */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search media..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              onClick={() => setShowAddUrl(!showAddUrl)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 whitespace-nowrap"
            >
              <Plus className="w-4 h-4" />
              Add URL
            </button>
          </div>

          {/* Add URL Form */}
          {showAddUrl && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg space-y-3">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <LinkIcon className="w-4 h-4" />
                Add New Media URL
              </h3>

              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Image/Video URL *</label>
                  <input
                    type="url"
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    placeholder="https://example.com/image.jpg"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    value={urlName}
                    onChange={(e) => setUrlName(e.target.value)}
                    placeholder="e.g., Hero Background"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={urlCategory}
                    onChange={(e) => setUrlCategory(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {categories.filter(c => c.value !== 'all').map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>

                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Alt Text (Optional)</label>
                  <input
                    type="text"
                    value={urlAltText}
                    onChange={(e) => setUrlAltText(e.target.value)}
                    placeholder="Description for accessibility"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={handleAddUrl}
                  disabled={adding}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50"
                >
                  {adding ? 'Adding...' : 'Add to Library & Select'}
                </button>
                <button
                  onClick={() => {
                    setShowAddUrl(false);
                    setUrlInput('');
                    setUrlName('');
                    setUrlCategory('general');
                    setUrlAltText('');
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Media Grid */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
              <p className="text-gray-600">Loading media library...</p>
            </div>
          ) : mediaItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <ImageIcon className="w-20 h-20 mb-4" />
              <p className="text-lg font-medium">No media found</p>
              <p className="text-sm">Try different filters or add a new URL</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {mediaItems.map((item) => (
                <div
                  key={item.id}
                  onClick={() => setSelectedUrl(item.url)}
                  onDoubleClick={() => handleQuickSelect(item.url)}
                  className={`relative group border-2 rounded-lg overflow-hidden cursor-pointer transition-all ${
                    selectedUrl === item.url
                      ? 'border-blue-600 shadow-lg ring-2 ring-blue-200'
                      : 'border-gray-200 hover:border-blue-400'
                  }`}
                >
                  {/* Selected Indicator */}
                  {selectedUrl === item.url && (
                    <div className="absolute top-2 left-2 z-10 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  )}

                  {/* Category Badge */}
                  <div className="absolute top-2 right-2 z-10">
                    <span className="px-2 py-1 bg-black bg-opacity-70 text-white text-xs rounded-full flex items-center gap-1">
                      <Tag className="w-3 h-3" />
                      {item.category}
                    </span>
                  </div>

                  {/* Media Type Icon */}
                  {isVideo(item.url) && (
                    <div className="absolute bottom-2 left-2 z-10">
                      <span className="px-2 py-1 bg-black bg-opacity-70 text-white text-xs rounded-full flex items-center gap-1">
                        <Film className="w-3 h-3" />
                        Video
                      </span>
                    </div>
                  )}

                  {/* Preview */}
                  <div className="aspect-square bg-gray-100">
                    {isVideo(item.url) ? (
                      <video
                        src={item.url}
                        className="w-full h-full object-cover"
                        muted
                        loop
                        onMouseEnter={(e) => e.target.play()}
                        onMouseLeave={(e) => e.target.pause()}
                      />
                    ) : (
                      <img
                        src={item.url}
                        alt={item.alt_text || item.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%23f3f4f6" width="400" height="300"/%3E%3Ctext fill="%239ca3af" font-family="sans-serif" font-size="14" dy="10.5" font-weight="400" x="50%25" y="50%25" text-anchor="middle"%3EFailed to Load%3C/text%3E%3C/svg%3E';
                        }}
                      />
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-2 bg-white border-t border-gray-200">
                    <p className="text-xs font-medium text-gray-900 truncate" title={item.name}>
                      {item.name}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex items-center justify-between bg-gray-50">
          <div className="text-sm text-gray-600">
            {selectedUrl ? (
              <span className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-600" />
                Media selected
              </span>
            ) : (
              <span>Select a media item or add new URL</span>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmSelection}
              disabled={!selectedUrl}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Use Selected Media
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
